import { SortableContext, rectSwappingStrategy } from '@dnd-kit/sortable';
import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { Badge, Button, Grid } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useParams } from 'react-router';

import { VideaTypography } from '../../../../../../common-components/elements/VideaTypography/VideaTypography';
import {
  blueBlack400,
  blueUltramarine070,
} from '../../../../../../theme/videa-style-tokens';
import { selectTeethAtom } from '../../../../../patientList/atoms';
import { selectedFindingsAtom } from '../../../../casePresentation/atoms';
import type { ImageObject } from '../../../../types';
import {
  enabledImageTypesAtom,
  imageBrowserExpandedAtom,
  showingAllAtom,
} from '../atoms';
import { useExtraImages } from '../helpers';

import { DroppableContainer } from './droppableContainer/DroppableContainer';
import { Image } from './image/Image';

const StyledImageBucket = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 0 0.75em 0;
  width: 100%;
  overflow: none;
  .show-all {
    background-color: black;
    border-radius: 0.5em;
    height: 3.5em;
    width: 5em;
    color: ${blueUltramarine070};
    display: flex;
    justify-content: left;
    align-items: center;
    flex-direction: row;
    gap: 1em;
    .text {
      width: 4em;
      font-size: 0.75em;
    }
    .hide-text {
      width: 5em;
      font-size: 0.5em;
    }
    :hover {
      background-color: ${blueBlack400};
    }
  }
`;

const StyledImageGrid = styled(Grid)`
  height: 100%;
`;

const NoImagesMessage = styled.p`
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  position: absolute;
  width: fit-content;
`;

export const ImageBucket = ({
  isImageBrowserOpen,
  otherImages,
  lastPage,
}: {
  isImageBrowserOpen: boolean;
  otherImages?: ImageObject[];
  lastPage?: boolean;
}) => {
  const isImageBrowserExpanded = useAtomValue(imageBrowserExpandedAtom);
  const { hiddenImages, outOfScope } = useExtraImages();
  const [isShowingAll, setIsShowingAll] = useAtom(showingAllAtom);
  const setEnabledImageTypes = useSetAtom(enabledImageTypesAtom);
  const presentSingleFinding = useAtomValue(selectedFindingsAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);

  const showButton = !presentSingleFinding && !selectedTeeth.length;

  const { imageId: activeImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();

  if (!isImageBrowserOpen || !otherImages) {
    return null;
  }
  return (
    <SortableContext
      items={otherImages.map((i) => i.imageId)}
      strategy={rectSwappingStrategy}
      id="image-bucket"
      disabled={!isImageBrowserOpen}
    >
      {!isImageBrowserExpanded ? (
        <StyledImageBucket >
          {otherImages?.map(
            (image, i) =>
              image && <Image image={image} index={i} key={image.imageId} />,
          )}
          {showButton && !!hiddenImages && lastPage && (
            <Button
              className="show-all"
              onClick={() => {
                setEnabledImageTypes([
                  'dentalPhotos',
                  'fmxPhotos',
                  'panoPhotos',
                ]);
                setIsShowingAll(true);
              }}
            >
              <VideaTypography variant="labelSmall" className="text">
                <Trans>SHOW ALL</Trans>
              </VideaTypography>
              <Badge badgeContent={hiddenImages} />
            </Button>
          )}
          {showButton &&
            !hiddenImages &&
            isShowingAll &&
            !!outOfScope &&
            lastPage && (
              <Button
                className="show-all"
                onClick={() => {
                  setIsShowingAll(false);
                }}
              >
                <VideaTypography className="hide-text">
                  <Trans>HIDE EXTRA IMAGES</Trans>
                </VideaTypography>
                <Badge badgeContent={outOfScope} className="badge" />
              </Button>
            )}
          {!activeImageId && (
            <DroppableContainer  />
          )}
          {otherImages?.length === 0 && (
            <NoImagesMessage>No additional images available</NoImagesMessage>
          )}
        </StyledImageBucket>
      ) : (
        <StyledImageGrid container spacing={2} columns={3}>
          {otherImages?.map(
            (image, i) =>
              image && (
                <Grid item key={i} xs={1} className="item">
                  <Image image={image} index={i} key={image.imageId} />
                </Grid>
              ),
          )}
        </StyledImageGrid>
      )}
    </SortableContext>
  );
};
